import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import getCookie from "../utils/getCookies";
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { DateTime, Interval } from "luxon";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { ColorModeContext, tokens } from "../theme";
import { DEFAULT_MAKEUP_START_TIMES } from '../utils/makeupScheduleDefaults';

// Fixed-date federal holidays (must match FEDERAL_HOLIDAYS in server helpers/makeup_helper.js).
// Floating holidays — Memorial Day, Labor Day, Thanksgiving — move every year and are computed in getFloatingHolidays.
const FEDERAL_HOLIDAYS = [
  { month: 1, day: 1 }, // New Year's Day
  { month: 7, day: 4 }, // Independence Day
  { month: 12, day: 25 }, // Christmas Day
];

//helper function to find the nth occurrence of a weekday in a month
//weekday uses Luxon numbering (1 = Monday ... 7 = Sunday); n = 1 for the first, n = -1 for the last
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  const firstOfMonth = DateTime.fromObject({ year, month, day: 1 }, { zone: "America/New_York" });

  if (n === -1) {
    let date = firstOfMonth.endOf("month").startOf("day");
    while (date.weekday !== weekday) {
      date = date.minus({ days: 1 });
    }
    return date;
  }

  let date = firstOfMonth;
  while (date.weekday !== weekday) {
    date = date.plus({ days: 1 });
  }
  return date.plus({ weeks: n - 1 });
};

//helper function to calculate the floating federal holidays for a given year
//Memorial Day: last Monday of May; Labor Day: first Monday of September; Thanksgiving break: Thu–Sun
const getFloatingHolidays = (year) => {
  const memorialDay = nthWeekdayOfMonth(year, 5, 1, -1); // 1 is Monday in Luxon
  const laborDay = nthWeekdayOfMonth(year, 9, 1, 1);
  const thanksgiving = nthWeekdayOfMonth(year, 11, 4, 4); // 4 is Thursday in Luxon

  return [
    { month: memorialDay.month, day: memorialDay.day },
    { month: laborDay.month, day: laborDay.day },
    ...[0, 1, 2, 3].map((offset) => {
      const d = thanksgiving.plus({ days: offset });
      return { month: d.month, day: d.day };
    }),
  ];
};

//helper function to check if a date is a federal holiday
const isFederalHoliday = (date) => {
  const matchesDate = (holiday) => date.month === holiday.month && date.day === holiday.day;
  if (FEDERAL_HOLIDAYS.some(matchesDate)) return true;
  return getFloatingHolidays(date.year).some(matchesDate);
};

//helper function to check if a date is a company vacation
const isCompanyVacation = (date, vacations) => {
  return vacations.includes(date.toISODate());
};

//helper function to check if a date is unavailable (federal holiday or company vacation)
const isDateUnavailable = (date, vacations) => {
  return isFederalHoliday(date) || isCompanyVacation(date, vacations);
};


function TeacherForm() {
  const { tid } = useParams();
  const [allowSunday, setAllowSunday] = useState(false);
  const [isPrivateCourse, setIsPrivateCourse] = useState(false);
  const [courseName, setCourseName] = useState("");
  const [ogCourseDate, setOgCourseDate] = useState("");
  const [ogDays, setOgDays] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [selectedWindows, setSelectedWindows] = useState([]);
  const [status, setStatus] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [courseCode, setCourseCode] = useState("");
  const [companyVacations, setCompanyVacations] = useState([]);
  const startTimesCacheRef = useRef(new Map());
  const [windowAvailability, setWindowAvailability] = useState({});
  const [isComputingAvailability, setIsComputingAvailability] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);

  useEffect(() => {
    const fetchScheduleData = async () => {
      try {
        const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/makeup/teacher/${tid}`);
        if (response.status === 200) {
          if (response.data.message && response.data.message.includes("Schedule has already been selected")) {
            setStatus(3);
            setShowSuccessScreen(true);
            return;
          }
          setScheduleData(response.data);
          setIsPrivateCourse(response.data.is_private_course);
          setCourseName(response.data.course_name);
          setOgCourseDate(response.data.og_course_date);
          setOgDays(response.data.og_days);
          setStatus(response.data.status);
          setAllowSunday(response.data.allow_sunday);
          setIsManual(response.data.is_manual);
          setCourseCode(response.data.course_code);
          console.log(response.data);
        }
      } catch (err) {
        console.log("Error in fetchScheduleData:", err);
        setError("Error fetching schedule data");
        setErrorStatus(err.response?.status);
      }
    };

    const fetchVacationDates = async () => {
      try {
        const token = getCookie('_auth');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await Axios.get(
          `${process.env.REACT_APP_URL}/api/v1/calendar-events/vacations`,
          { headers }
        );
        if (response.status === 200 && response.data) {
          const vacationDays = [];
          for (const vacation of response.data) {
            let current = DateTime.fromISO(vacation.from, { zone: "America/New_York" })
              .startOf("day");
            const end = DateTime.fromISO(vacation.to, { zone: "America/New_York" })
              .startOf("day");
            while (current <= end) {
              vacationDays.push(current.toISODate());
              current = current.plus({ days: 1 });
            }
          }
          setCompanyVacations(vacationDays);
          console.log("Loaded vacation days:", vacationDays);
        }
      } catch (err) {
        console.log("Error fetching vacation dates:", err);
      }
    };

    if (tid) {
      fetchScheduleData();
      fetchVacationDates();
    }
  }, [tid]);

  // Fetch makeup start times for a specific date
  const fetchStartTimesForDate = async (date) => {
    // Check cache first
    const dateStr = date.toISODate();
    if (startTimesCacheRef.current.has(dateStr)) {
      return startTimesCacheRef.current.get(dateStr);
    }

    try {
      const token = getCookie('_auth');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await Axios.get(
        `${process.env.REACT_APP_URL}/api/v1/makeup-start-times?day=${dateStr}`,
        { headers }
      );
      
      if (response.status === 200 && response.data && response.data.length > 0) {
        // Use the most recent valid configuration
        const config = response.data[0];
        startTimesCacheRef.current.set(dateStr, config);
        return config;
      }
    } catch (err) {
      console.log(`No start times config for ${dateStr}, using defaults`);
    }
    
    // Return null to indicate no config found (will use defaults)
    startTimesCacheRef.current.set(dateStr, null);
    return null;
  };

  // Generate 30-min intervals from 13:00 to 22:30 EST to include Sunday start times
  const getTimeIntervals = () => {
    const intervals = [];
    const start = DateTime.fromObject({ hour: 13, minute: 0 }, { zone: "America/New_York" });
    const end = DateTime.fromObject({ hour: 22, minute: 30 }, { zone: "America/New_York" });

    const localInterval = [];
    const localStart = start.toLocal()

    let current = start;
    let localCurrent = localStart;
    while (current < end) {
      const next = current.plus({ minutes: 30 });
      const localNext = localCurrent.plus({ minutes: 30 });
      
      intervals.push({
        start: current,
        end: next,
        label: `${current.toFormat('HH:mm')} - ${next.toFormat('HH:mm')}`,
        timeKey: current.toFormat('HH:mm')
      });
      localInterval.push({
        start: localCurrent,
        end: localNext,
        label: `${localCurrent.toFormat('HH:mm')} - ${localNext.toFormat('HH:mm')}`,
        timeKey: localCurrent.toFormat('HH:mm'),
      });
      current = next;
      localCurrent = localNext;
    }
    return { intervals, localInterval };
  };

  // Generate days (x-axis) using Luxon, include Sundays if allowSunday is true
  const getDays = () => {
    if (!scheduleData) return [];
    const { window } = scheduleData;
    const days = [];
    let current = DateTime.fromISO(window.start_date, { zone: "America/New_York" }).startOf('day');
    const end = DateTime.fromISO(window.end_date, { zone: "America/New_York" }).endOf('day');
    while (current <= end) {
      // Include Sundays if allowSunday is true, otherwise skip them
      // But always include vacation days to show them as unavailable
      const isVacation = isDateUnavailable(current, companyVacations);
      if (current.weekday !== 7 || allowSunday || isVacation) {
        const isOgDay = ogDays.includes(current.weekday.toString());
        const isSunday = current.weekday === 7;
        days.push({
          date: current,
          label: `${current.toFormat('MMM d')} (${current.toFormat('ccc')})`,
          dayKey: current.toFormat('yyyy-MM-dd'),
          isSunday: isSunday,
          isOgDay: isOgDay,
          isVacation: isVacation
        });
      }
      current = current.plus({ days: 1 });
    }
    return days;
  };

  // New function to check if a slot is blocked (only from backend blockedTimes)
  const isSlotBlocked = (day, startIdx, intervals) => {
    if (!scheduleData || day.isOgDay) return false;
    
    const { blockedTimes } = scheduleData;
    if (!blockedTimes) return false;
    
    const sStart = day.date.set({ hour: intervals[startIdx].start.hour, minute: intervals[startIdx].start.minute, second: 0, millisecond: 0 });
    const sEnd = sStart.plus({ minutes: 30 });
    
    return blockedTimes.some(blocked => {
      const blockedStart = DateTime.fromISO(blocked.start, { zone: "America/New_York" });
      const blockedEnd = DateTime.fromISO(blocked.end, { zone: "America/New_York" });
      const overlap = Interval.fromDateTimes(sStart, sEnd).overlaps(Interval.fromDateTimes(blockedStart, blockedEnd));
      return overlap;
    });
  };

  // Update checkWindowAvailable to handle Sunday special case and vacation dates
  const checkWindowAvailable = async (day, startIdx, intervals) => {
    if (!scheduleData) return false;
    
    if (day.isOgDay) {
      // Skip logging for OG days as they should be unavailable
      return false;
    }
    
    if (day.isVacation) {
      // Skip logging for vacation days as they should be unavailable
      return false;
    }
    
    // Check if this date or any dates in the window are vacation days
    const { og_duration } = scheduleData;
    const numSlots = Math.ceil(og_duration / 30);
    const slotStart = day.date.set({ hour: intervals[startIdx].start.hour, minute: intervals[startIdx].start.minute, second: 0, millisecond: 0 });
    const slotEnd = slotStart.plus({ minutes: og_duration });
    
    // Check if any part of the window falls on a vacation day
    let checkDate = slotStart.startOf('day');
    const endDate = slotEnd.startOf('day');
    while (checkDate <= endDate) {
      if (isDateUnavailable(checkDate, companyVacations)) {
        // console.log(`${debugKey}: Overlaps with vacation on ${checkDate.toISODate()}`);
        return false;
      }
      checkDate = checkDate.plus({ days: 1 });
    }
    
    // Get start times configuration for this specific date
    const startTimesConfig = await fetchStartTimesForDate(day.date);
    
    // Determine valid start times based on day type and config
    let validStartTimes;
    if (startTimesConfig) {
      // Check if it's Sunday and use only Sunday start times
      if (day.isSunday && startTimesConfig.sunday_start_times && startTimesConfig.sunday_start_times.length > 0) {
        validStartTimes = [...startTimesConfig.sunday_start_times];
      } 
      // Check if it's Saturday and use only Saturday start times
      else if (day.date.weekday === 6 && startTimesConfig.saturday_start_times && startTimesConfig.saturday_start_times.length > 0) {
        validStartTimes = [...startTimesConfig.saturday_start_times];
      } 
      // For weekdays or when weekend-specific times aren't available, use valid_start_times
      else {
        validStartTimes = [...startTimesConfig.valid_start_times];
      }
    } else {
      // Use default times as fallback
      if (day.isSunday && allowSunday) {
        // Default Sunday times only
        validStartTimes = DEFAULT_MAKEUP_START_TIMES.sunday;
      } else if (day.date.weekday === 6) {
        // Saturday default times
        validStartTimes = DEFAULT_MAKEUP_START_TIMES.saturday;
      } else {
        // Weekday default times
        validStartTimes = DEFAULT_MAKEUP_START_TIMES.weekday;
      }
    }
    
    const startTimeKey = intervals[startIdx].timeKey;
    
    // Check if this is a valid starting time for this date
    if (!validStartTimes.includes(startTimeKey)) {
      // Not a valid start time for this slot
      return false;
    }
    
    if (startIdx + numSlots - 1 >= intervals.length) return false;
    
    // For Sundays, the window must end at or before 22:30 (same as regular days)
    const latestEnd = day.date.set({ hour: 22, minute: 30, second: 0, millisecond: 0 });
    if (slotEnd > latestEnd.plus({ minutes: 0.5 })) return false;
    
    // Check for blocked times (only for non-Sundays since Sundays don't have blocked times)
    if (!day.isSunday) {
      for (let i = 0; i < numSlots; i++) {
        if (isSlotBlocked(day, startIdx + i, intervals)) {
          return false;
        }
      }
    }
    
    return true; // This slot is available!
  };

  // Helper: check if a new window would overlap with existing selections
  const wouldOverlapExisting = (dayKey, startIdx) => {
    if (!selectedWindows.length || !scheduleData) return false;
    const { og_duration } = scheduleData;
    const numSlots = Math.ceil(og_duration / 30);
    
    return selectedWindows.some(window => {
      if (window.dayKey !== dayKey) return false;
      
      const newWindowStart = startIdx;
      const newWindowEnd = startIdx + numSlots - 1;
      const existingWindowStart = window.startIdx;
      const existingWindowEnd = window.startIdx + numSlots - 1;
      
      // Check for overlap: new window overlaps with existing if:
      // new start is before existing end AND new end is after existing start
      return newWindowStart <= existingWindowEnd && newWindowEnd >= existingWindowStart;
    });
  };

  const handleCellClick = (dayKey, startIdx, available) => {
    if (!available) return;
    
    if (isPrivateCourse) {
      // For private courses, allow up to 2 selections
      setSelectedWindows(prev => {
        const newWindows = [...prev];
        const existingIndex = newWindows.findIndex(w => w.dayKey === dayKey && w.startIdx === startIdx);
        
        if (existingIndex !== -1) {
          // If already selected, remove it
          newWindows.splice(existingIndex, 1);
        } else if (newWindows.length < 2 && !wouldOverlapExisting(dayKey, startIdx)) {
          // If not selected, we have room, and it doesn't overlap, add it
          newWindows.push({ dayKey, startIdx });
        }
        return newWindows;
      });
    } else {
      // For non-private courses, allow toggling selection
      setSelectedWindows(prev => {
        const existingIndex = prev.findIndex(w => w.dayKey === dayKey && w.startIdx === startIdx);
        if (existingIndex !== -1) {
          // If already selected, remove it
          return prev.filter((_, index) => index !== existingIndex);
        } else {
          // If not selected, replace with this selection
          return [{ dayKey, startIdx }];
        }
      });
    }
  };

  const formatSelectedWindows = () => {
    if (!selectedWindows.length || !scheduleData) return [];
    const { intervals } = getTimeIntervals();
    const days = getDays();
    const { og_duration } = scheduleData; // duration in minutes

    return selectedWindows.map(window => {
      const day = days.find(d => d.dayKey === window.dayKey);
      const startSlot = intervals[window.startIdx];
      // Parse start time from slot label (e.g., '19:00 - 19:30')
      const [startHour, startMinute] = startSlot.label.split(' - ')[0].split(':').map(Number);
      // Create a DateTime object for the start
      const startDate = day.date.set({ hour: startHour, minute: startMinute, second: 0, millisecond: 0 });
      // Add duration to get end time
      const endDate = startDate.plus({ minutes: og_duration });
      const startLabel = startDate.toFormat('HH:mm');
      const endLabel = endDate.toFormat('HH:mm');
      return `${day.label} at ${startLabel} - ${endLabel} EST`;
    });
  };

  const handleSubmit = () => {
    setOpenConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      const { og_duration } = scheduleData;
      const days = getDays();
      const {intervals} = getTimeIntervals();

      const selectedWindowsData = selectedWindows.map(window => {
        const day = days.find(d => d.dayKey === window.dayKey);
        const startSlot = intervals[window.startIdx];
        const start = day.date.set({ hour: startSlot.start.hour, minute: startSlot.start.minute, second: 0, millisecond: 0 });
        const end = start.plus({ minutes: og_duration });
        return { start: start.toUTC().toISO(), end: end.toUTC().toISO() };
      });

      const response = await Axios.post(
        `${process.env.REACT_APP_URL}/api/v1/makeup/teacher/${tid}`,
        {
          selected_window: selectedWindowsData,
          is_private_course: isPrivateCourse
        }
      );
      if (response.status !== 200) {
        setModalError(response.data.error);
        setErrorStatus(response.status);
      } else{
        setOpenConfirmModal(false);
        setShowSuccessScreen(true);
      }
    } catch (err) {
      // Extract the specific error message from the API response
      const errorMessage = err.response?.data?.error || "Error submitting schedule";
      setModalError(errorMessage);
      setErrorStatus(err.response?.status);
    }
  };

  const handleReschedule = async () => {
    try{
      const response = await Axios.post(
        `${process.env.REACT_APP_URL}/api/v1/makeup/teacher/${tid}/reschedule`,
      );
      if (response.status === 200) {
        window.location.reload();
      } else{
        setError("Your request cannot be processed at the moment. Please try again later.");
        setErrorStatus(response.status);
      }
    }catch(err){
      setError("Error processing your request");
      setErrorStatus(err.response?.status);
    }
  };

  const {intervals, localInterval} = useMemo(() => getTimeIntervals(), []);
  const days = useMemo(() => getDays(), [scheduleData, ogDays, companyVacations, allowSunday]);

  // Precompute window availability for all slots
  useEffect(() => {
    const computeAvailability = async () => {
      if (!scheduleData || days.length === 0 || intervals.length === 0) {
        return;
      }
      setIsComputingAvailability(true);
      
      const availability = {};
      let computedCount = 0;
      
      for (const day of days) {
        for (let i = 0; i < intervals.length; i++) {
          const key = `${day.dayKey}-${i}`;
          try {
            availability[key] = await checkWindowAvailable(day, i, intervals);
            computedCount++;
          } catch (err) {
            console.error(`Error computing availability for ${key}:`, err);
            availability[key] = false;
          }
        }
      }
      
      setWindowAvailability(availability);
      setIsComputingAvailability(false);
      
    };
    
    // Clear cache when dependencies change
    startTimesCacheRef.current.clear();
    computeAvailability();
  }, [scheduleData, allowSunday, companyVacations, days.length, intervals.length]); // Include array lengths to avoid reference issues

  // Helper function to check if window is available (using precomputed values)
  const isWindowAvailable = (day, startIdx) => {
    if (isComputingAvailability) return false; // Don't show anything as available while computing
    const key = `${day.dayKey}-${startIdx}`;
    const result = windowAvailability[key] === true;
    
    
    return result;
  };

  // Helper: check if a slot overlaps with any selected window (excluding itself)
  const doesSlotOverlapSelected = (dayKey, startIdx) => {
    if (!selectedWindows.length || !scheduleData) return false;
    const { og_duration } = scheduleData;
    const numSlots = Math.ceil(og_duration / 30);
    // For each selected window, check if this slot is within its range
    return selectedWindows.some(window => {
      if (window.dayKey !== dayKey) return false;
      const windowStart = window.startIdx;
      const windowEnd = window.startIdx + numSlots - 1;
      return startIdx >= windowStart && startIdx <= windowEnd;
    });
  };

  // Helper: check if this slot is part of a selected window
  const isSlotSelected = (dayKey, startIdx) => {
    if (!selectedWindows.length || !scheduleData) return false;
    const { og_duration } = scheduleData;
    const numSlots = Math.ceil(og_duration / 30);
    return selectedWindows.some(window => {
      if (window.dayKey !== dayKey) return false;
      const windowStart = window.startIdx;
      const windowEnd = window.startIdx + numSlots - 1;
      return startIdx >= windowStart && startIdx <= windowEnd;
    });
  };

  // Helper: check if this slot would be selectable (for private courses, check overlap)
  const isSlotSelectable = (dayKey, startIdx, available) => {
    if (!available) return false;
    if (!isPrivateCourse) return true;
    
    // For private courses, check if this would overlap with existing selections
    // BUT allow deselection of already selected slots
    const isAlreadySelected = selectedWindows.some(window => 
      window.dayKey === dayKey && window.startIdx === startIdx
    );
    if (isAlreadySelected) return true;
    
    return !wouldOverlapExisting(dayKey, startIdx);
  };

  // Success Screen Component
  const SuccessScreen = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <CheckCircleIcon
          sx={{
            fontSize: 80,
            color: colors.greenAccent[500],
            mb: 3
          }}
        />

        <Typography
          variant="h2"
          sx={{
            mb: 3,
            color: colors.primary[100],
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2
          }}
        >
          You have successfully submitted your schedule request
        </Typography>

        {courseName && ogCourseDate && (
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: '1.1rem', md: '1.3rem' }
            }}
          >
            ({courseCode}) {courseName} ({isPrivateCourse ? "1:1" : "Group"}) • {DateTime.fromISO(ogCourseDate).toFormat('MMMM d, yyyy')}
          </Typography>
        )}

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexDirection: 'column',
          maxWidth: 500
        }}>
          <Typography
            sx={{
              color: colors.grey[300],
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            Once the student confirms the schedule, we'll let you know!
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Error Screen Component
  const ErrorScreen = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <Typography
          variant="h2"
          sx={{
            mb: 3,
            color: colors.redAccent[500],
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2
          }}
        >
          This link has expired
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            color: colors.grey[300],
            fontWeight: 500,
            fontSize: { xs: '1.1rem', md: '1.3rem' }
          }}
        >
          Please contact your administrator or request a new link.
        </Typography>
      </Box>
    </Box>
  );

  // Show error screen only for 404 or 500
  if (error && (errorStatus === 404 || errorStatus === 500)) {
    return <ErrorScreen />;
  }

  // Show success screen if status is 3 or showSuccessScreen is true
  if (showSuccessScreen || status === 3) {
    return <SuccessScreen />;
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh", maxWidth: "1200px", margin: "0 auto" }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3, 
        mb: 4
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton
              onClick={colorMode.toggleColorMode}
              size="small"
              sx={{
                backgroundColor: colors.primary[600],
                color: colors.primary[100],
                '&:hover': {
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                },
              }}
            >
              {theme.palette.mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            </IconButton>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: colors.greenAccent[500]
            }} />
            <Typography sx={{
              color: colors.grey[300],
              fontSize: '0.9rem'
            }}>
              EST Timezone
            </Typography>
            {isComputingAvailability && (
              <Typography sx={{ color: colors.grey[300], fontSize: '0.9rem', mt: 1 }}>
                Loading available time slots...
              </Typography>
            )}
          </Box>
        </Box>
        
        <Box sx={{
          textAlign: 'center',
          py: 4
        }}>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              color: colors.primary[100],
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {isManual ? "Class Scheduling" : "Makeup Schedule"}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              mb: 3,
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: '1.2rem', md: '1.4rem' }
            }}
          >
            for <strong>({courseCode}) {courseName}</strong> {!isManual && "on" } <strong>{!isManual && DateTime.fromISO(ogCourseDate).toFormat('MMMM d, yyyy')}</strong>
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexDirection: 'column'
          }}>
            <Typography
              sx={{
                color: colors.grey[300],
                fontSize: '0.95rem'
              }}
            >
              {isPrivateCourse
                ? "Select two available slots and they will automatically give you the windows."
                : "Select one available slot and it will automatically give you the window."}
            </Typography>

            <Box sx={{
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: '#FFF3E0'
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#ED6C02'
              }} />
              <Typography sx={{
                color: '#B76E00',
                fontSize: '0.9rem'
              }}>
                Note: You can only request one extension and schedule cannot be modified after submission
              </Typography>
            </Box>
            
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              justifyContent: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  bgcolor: theme.palette.mode === 'dark' ? colors.grey[600] : colors.grey[800]
                }} />
                <Typography sx={{ fontSize: '0.85rem', color: colors.grey[300] }}>
                  Unavailable
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : theme.palette.background.paper,
                  border: '1px solid',
                  borderColor: theme.palette.divider
                }} />
                <Typography sx={{ fontSize: '0.85rem', color: colors.grey[300] }}>
                  Available
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  bgcolor: colors.greenAccent[500]
                }} />
                <Typography sx={{ fontSize: '0.85rem', color: colors.grey[300] }}>
                  Selected
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {!scheduleData && !error && (
        <Typography>Loading schedule data...</Typography>
      )}
      {error && errorStatus !== 404 && errorStatus !== 500 && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {scheduleData && (
        <>
          {companyVacations.length > 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2
            }}>
              <Alert severity="info" sx={{ maxWidth: 800 }}>
                Some dates are unavailable due to federal holidays or company vacations. These dates have been automatically excluded from the scheduling options.
              </Alert>
            </Box>
          )}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%' 
          }}>
            <Box sx={{ 
              overflowX: 'auto', 
              width: '100%',
              maxWidth: '100%'
            }}>
              <table style={{ 
              borderCollapse: 'collapse', 
              minWidth: days.length * 140 + 100,
              width: '100%'
            }}>
              <thead>
                <tr>
                  <th style={{
                    width: 120,
                    background: theme.palette.mode === 'dark' ? colors.primary[600] : theme.palette.grey[100],
                    position: 'sticky',
                    left: 0,
                    zIndex: 2
                  }}></th>
                  {days.map(day => (
                    <th key={day.dayKey} style={{
                      background: theme.palette.mode === 'dark' ? colors.primary[600] : theme.palette.grey[100],
                      color: theme.palette.mode === 'dark' ? colors.grey[100] : 'inherit',
                      minWidth: 140,
                      width: 140,
                      textAlign: 'center',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1
                    }}>{day.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {intervals.map((interval, rowIdx) => (
                  <tr 
                    key={interval.timeKey}
                    style={{
                      borderCollapse: 'collapse'
                    }}
                  >
                    <td style={{ 
                      position: 'sticky', 
                      left: 0, 
                      zIndex: 1, 
                      minWidth: 120,
                      width: 120,
                      textAlign: 'center'
                    }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '1rem',
                          whiteSpace: 'nowrap',
                          color: interval.isStartingTime ? colors.greenAccent[600] : 'inherit',
                          fontWeight: interval.isStartingTime ? '600' : 'normal',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          position: 'relative',
                          backgroundColor: interval.isStartingTime ? colors.greenAccent[800] : (theme.palette.mode === 'dark' ? colors.grey[600] : colors.grey[800]),
                          padding: '4px 8px',
                          borderRadius: '10px',
                          width: '100%'
                        }}
                      >
                        {interval.isStartingTime && (
                          <Box
                            component="span"
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: colors.greenAccent[500],
                              display: 'inline-block',
                              boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)'
                            }}
                          />
                        )}
                        {interval.label} EST
                      </Typography>
                      {Intl.DateTimeFormat().resolvedOptions().timeZone !== 'America/New_York' && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '1rem',
                            whiteSpace: 'nowrap',
                            color: interval.isStartingTime ? colors.greenAccent[600] : 'inherit',
                            fontWeight: interval.isStartingTime ? '600' : 'normal'
                          }}
                        >
                          ({localInterval[rowIdx].label})
                        </Typography>
                      )}
                    </td>
                    {days.map(day => {
                      const available = isWindowAvailable(day, rowIdx);
                      const overlapsSelected = doesSlotOverlapSelected(day.dayKey, rowIdx);
                      const isSelected = isSlotSelected(day.dayKey, rowIdx);
                      const selectable = isSlotSelectable(day.dayKey, rowIdx, available);
                      const isBlocked = isSlotBlocked(day, rowIdx, intervals);
                      
                      // Slot is selectable if available and (not overlapping or is part of a selected window)
                      // OR if it's already selected (for deselection)
                      const canSelect = (selectable && (!overlapsSelected || isSelected) && !day.isOgDay) || isSelected;
                      
                      // Determine slot status and styling
                      let slotStatus = "Available";
                      let isClickable = canSelect;
                      let tooltipText = "";
                      
                      if (isSelected) {
                        slotStatus = "Selected";
                        isClickable = true;
                      } else if (day.isVacation) {
                        slotStatus = "Unavailable";
                        isClickable = false;
                        tooltipText = "Federal holiday or company vacation";
                      } else if (day.isOgDay) {
                        slotStatus = "Unavailable";
                        isClickable = false;
                        tooltipText = "Regular class day - unavailable for makeup";
                      } else if (day.isSunday) {
                        // For Sundays, check if allowSunday is true and if this is a valid Sunday time
                        if (allowSunday && available) {
                          slotStatus = overlapsSelected && !isSelected ? "Unavailable" : "Available";
                          isClickable = canSelect;
                        } else {
                          slotStatus = "Unavailable";
                          isClickable = false;
                        }
                      } else if (isBlocked) {
                        slotStatus = "Unavailable";
                        isClickable = false;
                      } else if (available) {
                        // Slot is available - this is a valid starting time
                        slotStatus = overlapsSelected && !isSelected ? "Unavailable" : "Available";
                        isClickable = canSelect;
                      } else {
                        // Not available
                        slotStatus = "Unavailable";
                        isClickable = false;
                      }
                      
                      const cellContent = (
                        <Paper
                          sx={{
                            m: 0.5,
                            p: 1.5,
                            height: 48,
                            cursor: isClickable ? "pointer" : "not-allowed",
                            bgcolor:
                              slotStatus === 'Selected'
                                ? colors.greenAccent[500]
                                : slotStatus === 'Available'
                                ? (theme.palette.mode === 'dark' ? colors.primary[500] : theme.palette.background.paper)
                                : theme.palette.mode === 'dark' ? colors.grey[600] : colors.grey[800],
                            color: isSelected
                              ? colors.primary[500]
                              : "inherit",
                            textAlign: 'center',
                            transition: 'background 0.2s',
                            position: 'relative',
                            '&:hover': {
                              bgcolor: isClickable
                                ? colors.greenAccent[600]
                                : theme.palette.mode === 'dark' ? colors.grey[600] : colors.grey[800],
                              color: isClickable
                                ? colors.primary[500]
                                : "inherit",
                            }
                          }}
                          onClick={() => handleCellClick(day.dayKey, rowIdx, isClickable)}
                        >
                          {/* Visual indicator for available starting times */}
                          {slotStatus === "Available" && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: colors.greenAccent[500],
                                boxShadow: '0 0 3px rgba(76, 175, 80, 0.5)'
                              }}
                            />
                          )}
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.9rem',
                              lineHeight: 1.2,
                              wordBreak: 'break-word',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {slotStatus}
                          </Typography>
                        </Paper>
                      );
                      
                      return (
                        <td 
                          key={day.dayKey + '-' + interval.timeKey} 
                          style={{ 
                            padding: 0,
                            width: 140,
                            minWidth: 140
                          }}
                        >
                          {tooltipText ? (
                            <Tooltip title={tooltipText} arrow>
                              {cellContent}
                            </Tooltip>
                          ) : (
                            cellContent
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
        </>
      )}
      <Box sx={{ 
        mt: 6, 
        display: "flex", 
        flexDirection: "column",
        alignItems: "center", 
        gap: 3,
        pb: 12 
      }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          width: "100%",
          maxWidth: 400
        }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isPrivateCourse ? selectedWindows.length !== 2 : selectedWindows.length !== 1}
            sx={{
              width: "100%",
              minHeight: 56,
              fontSize: '1.1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              boxShadow: 3,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[600],
                boxShadow: 4,
                transform: "translateY(-1px)",
                transition: "all 0.2s ease-in-out"
              },
              '&:disabled': {
                bgcolor: colors.grey[300],
                color: colors.grey[100],
                transform: "none",
                boxShadow: 1
              }
            }}
          >
            Submit Selection
          </Button>
          {isPrivateCourse && selectedWindows.length < 2 && (
            <Typography sx={{ color: colors.redAccent[400], fontSize: '0.9rem' }}>
              Please select 2 time slots for 1:1 courses
            </Typography>
          )}

          <Tooltip
            title={status !== 1 ? "You have already requested an extension" : ""}
            arrow
          >
            <span style={{ width: "100%" }}> 
              <Button
                variant="outlined"
                disabled={status !== 1}
                onClick={handleReschedule}
                sx={{
                  width: "100%",
                  minHeight: 56,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderColor: colors.primary[100],
                  color: colors.primary[100],
                  borderRadius: 2,
                  textTransform: "none",
                  '&:hover': {
                    borderColor: colors.greenAccent[500],
                    transform: "translateY(-1px)",
                    transition: "all 0.2s ease-in-out"
                  }
                }}
              >
                Extend Window
              </Button>
            </span>
          </Tooltip>
          <Typography sx={{
            color: '#B76E00',
            fontSize: '0.9rem'
          }}>
            *If the extension does not add new dates, it means the current date is the last day available for scheduling.
          </Typography>
        </Box>
      </Box>
      <Dialog
        open={openConfirmModal}
        onClose={() => {
          setOpenConfirmModal(false);
          setModalError(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.primary[600] } }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 3,
          pb: 2,
          color: colors.primary[100],
          fontWeight: 600
        }}>
          Confirm Your Selection
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Typography sx={{ mb: 2, color: colors.grey[300] }}>
            You have selected the following window(s):
          </Typography>
          {formatSelectedWindows().map((window, index) => (
            <Typography
              key={index}
              sx={{
                pl: 2,
                mb: 1,
                color: colors.grey[300],
                '&::before': {
                  content: '"•"',
                  position: 'absolute',
                  left: 32,
                  color: colors.primary[100]
                }
              }}
            >
              {window}
            </Typography>
          ))}
          {isPrivateCourse && (
            <Typography sx={{ mt: 3, color: colors.grey[300] }}>
              Once the student confirms the schedule, we'll let you know!
            </Typography>
          )}
        </DialogContent>
        {modalError && (
          <DialogContent sx={{ px: 4, pb: 3 }}>
            <Typography sx={{ color: colors.redAccent[500] }}>{modalError}</Typography>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => {
              setOpenConfirmModal(false);
              setModalError(null);
            }}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              borderColor: colors.primary[100],
              color: colors.primary[100],
              borderRadius: 2,
              textTransform: "none"
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[600]
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TeacherForm;
